import {
  LayoutDashboard, BookOpen, Users, FolderKanban, Megaphone,
  FileText, BarChart3, Code2, type LucideIcon,
} from 'lucide-react';

/**
 * Two-level navigation model.
 *
 * The left vertical menu shows the top-level GROUPS below; selecting a group
 * navigates to its first sub-view. A horizontal tab bar (ContentTabs) then
 * exposes the group's sub-views as a second level inside the content area.
 *
 * `currentView` (in App.tsx) remains the single source of truth for the active
 * page; the active group is derived from which group contains it.
 */

export interface NavGroupItem {
  /** Matches a CurrentView id in App.tsx */
  id: string;
  /** German fallback label, used when no i18nKey resolves a translation. */
  label: string;
  /** Key in the `navigation` namespace (e.g. 'items.leads'); consumers translate via it. */
  i18nKey?: string;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Sub-views. A group with a single item renders no horizontal tab bar. */
  items: NavGroupItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: [{ id: 'dashboard', label: 'Dashboard', i18nKey: 'items.dashboard' }],
  },
  {
    id: 'guide',
    label: 'Guide',
    icon: BookOpen,
    items: [{ id: 'guide', label: 'Guide', i18nKey: 'items.guide' }],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: Users,
    items: [
      { id: 'leads', label: 'Kontakte', i18nKey: 'items.leads' },
      { id: 'companies', label: 'Unternehmen', i18nKey: 'items.companies' },
      { id: 'opportunities', label: 'Verkaufschancen', i18nKey: 'items.opportunities' },
      { id: 'customers', label: 'Kunden', i18nKey: 'items.customers' },
      { id: 'customer-intelligence', label: 'Kundenanalyse', i18nKey: 'items.customerIntelligence' },
    ],
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    items: [
      { id: 'tasks', label: 'Aufgaben', i18nKey: 'items.tasks' },
      { id: 'engagements', label: 'Aufträge', i18nKey: 'items.engagements' },
      { id: 'time-tracking', label: 'Zeiterfassung', i18nKey: 'items.timeTracking' },
      { id: 'expenses', label: 'Reisekosten', i18nKey: 'items.expenses' },
    ],
  },
  {
    id: 'development',
    label: 'Development',
    icon: Code2,
    items: [
      { id: 'development', label: 'Entwicklung', i18nKey: 'items.development' },
      { id: 'software-compliance', label: 'Software & Compliance', i18nKey: 'items.softwareCompliance' },
      { id: 'feedback', label: 'Ticket-System', i18nKey: 'items.feedback' },
      { id: 'echo-brain', label: 'Echo-Brain' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    items: [
      { id: 'google-ads-overview', label: 'Übersicht', i18nKey: 'items.googleAdsOverview' },
      { id: 'google-ads-campaigns', label: 'Kampagnen', i18nKey: 'items.googleAdsCampaigns' },
      { id: 'google-ads-optimization', label: 'Optimierung', i18nKey: 'items.googleAdsOptimization' },
      { id: 'google-ads-assets', label: 'Assets', i18nKey: 'items.googleAdsAssets' },
      { id: 'google-ads-templates', label: 'Vorlagen', i18nKey: 'items.googleAdsTemplates' },
      { id: 'google-ads-insights', label: 'Insights', i18nKey: 'items.googleAdsInsights' },
      { id: 'google-ads-merchant', label: 'Merchant', i18nKey: 'items.googleAdsMerchant' },
      { id: 'automate', label: 'Automate', i18nKey: 'items.automate' },
      { id: 'brandbook', label: 'Brandbook', i18nKey: 'items.brandbook' },
    ],
  },
  {
    id: 'documents',
    label: 'Dokumente',
    icon: FileText,
    items: [
      { id: 'offers', label: 'Angebote', i18nKey: 'items.offers' },
      { id: 'documents', label: 'Dokumente', i18nKey: 'items.documents' },
      { id: 'material', label: 'Material', i18nKey: 'items.material' },
      { id: 'emails', label: 'E-Mails', i18nKey: 'items.emails' },
      { id: 'products', label: 'Produkte', i18nKey: 'items.products' },
    ],
  },
  {
    id: 'bi',
    label: 'Business Intelligence',
    icon: BarChart3,
    items: [
      { id: 'insights', label: 'Insights', i18nKey: 'items.insights' },
      { id: 'ai-insights', label: 'KI-Insights', i18nKey: 'items.aiInsights' },
      { id: 'business-plan', label: 'Businessplan', i18nKey: 'items.businessPlan' },
      { id: 'team-skills', label: 'Team-Skills', i18nKey: 'items.teamSkills' },
      { id: 'stream', label: 'Stream', i18nKey: 'items.stream' },
    ],
  },
];

/** Map a view id to the group that contains it (if any). */
export function findGroupForView(viewId: string): NavGroup | undefined {
  return NAV_GROUPS.find(g => g.items.some(i => i.id === viewId));
}

/** The default view a group opens to (its first sub-view). */
export function defaultViewForGroup(group: NavGroup): string {
  return group.items[0]?.id ?? group.id;
}
